import React, { useState } from 'react';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { VscGraphLeft } from 'react-icons/vsc';
import styled from 'styled-components/macro';
import { Dropdown, Menu } from 'antd';

import { AddOrRemoveGovernanceFlagModal } from '../../../../EntityDropdown/AddOrRemoveGovernanceFlagModal';
import useDropdownKeyboard from '../../../../../../hooks/useDropdownKeyboard';
import { useTranslation } from '../../../../../../../i18n/useTranslation';
import { useEntityData, useRouteToTab } from '../../../../EntityContext';
import { MenuIcon } from '../../../../EntityDropdown/EntityDropdown';
import { SchemaField } from '../../../../../../../types.generated';
import { generateSchemaFieldUrn } from '../../../Lineage/utils';

export const ImpactAnalysisIcon = styled(VscGraphLeft)`
    transform: scaleX(-1);
    font-size: 18px;
`;

export const CopyOutlinedIcon = styled(CopyOutlined)`
    transform: scaleX(-1);
    font-size: 16px;
`;

const MenuItem = styled.div<{ $disabled?: boolean }>`
    align-items: center;
    display: flex;
    padding: 0 4px;
    color: ${(props) => (props.$disabled ? '#00000040' : '#262626')};
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
`;

const FIELD_PATH = 'fieldPath';
const COLUMN_URN = 'columnUrn';

interface Props {
    field: SchemaField;
    refetch?: () => void;
}

export default function MenuColumn({ field, refetch }: Props) {
    const { t } = useTranslation();
    const routeToTab = useRouteToTab();

    const entityData = useEntityData();

    const selectedColumnUrn = generateSchemaFieldUrn(field.fieldPath, entityData?.urn);
    const [isGovernanceFlagModalVisible, setIsGovernanceFlagModalVisible] = useState(false);
    const [copiedItem, setCopiedItem] = useState<typeof FIELD_PATH | typeof COLUMN_URN | null>(null);
    const { visible, setVisible, handleKeyDown, toggleDropdown } = useDropdownKeyboard();

    const handleCopy = (value: string, itemType: typeof FIELD_PATH | typeof COLUMN_URN) => {
        navigator.clipboard.writeText(value);
        setCopiedItem(itemType);
        setTimeout(() => setCopiedItem(null), 2000);
    };

    return (
        <>
            <Dropdown
                open={visible}
                overlay={
                    <Menu>
                        <Menu.Item key="0" tabIndex={0} aria-label={t('See Column Lineage')}>
                            <MenuItem
                                onClick={() =>
                                    routeToTab({ tabName: 'Lineage', tabParams: { column: field.fieldPath } })
                                }
                            >
                                <ImpactAnalysisIcon /> &nbsp; {t('See Column Lineage')}
                            </MenuItem>
                        </Menu.Item>
                        {navigator.clipboard && (
                            <Menu.Item key="1" aria-label={t('Copy Column Field Path')}>
                                <MenuItem onClick={() => handleCopy(field.fieldPath, FIELD_PATH)}>
                                    {copiedItem === FIELD_PATH ? (
                                        <CheckOutlined aria-hidden="true" />
                                    ) : (
                                        <CopyOutlinedIcon aria-hidden="true" />
                                    )}
                                    &nbsp; {t('Copy Column Field Path')}
                                </MenuItem>
                            </Menu.Item>
                        )}
                        {navigator.clipboard && (
                            <Menu.Item key="2" aria-label={t('Copy Column Urn')}>
                                <MenuItem onClick={() => handleCopy(selectedColumnUrn || '', COLUMN_URN)}>
                                    {copiedItem === COLUMN_URN ? (
                                        <CheckOutlined aria-hidden="true" />
                                    ) : (
                                        <CopyOutlinedIcon aria-hidden="true" />
                                    )}
                                    &nbsp; {t('Copy Column Urn')}
                                </MenuItem>
                            </Menu.Item>
                        )}
                    </Menu>
                }
                trigger={['click']}
                onOpenChange={(flag) => setVisible(flag)}
            >
                <MenuIcon
                    tabIndex={0}
                    fontSize={16}
                    aria-haspopup="true"
                    aria-label={t('More options')}
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                />
            </Dropdown>
            {isGovernanceFlagModalVisible && (
                <AddOrRemoveGovernanceFlagModal
                    entityData={entityData}
                    field={field}
                    refetch={refetch}
                    onClose={() => setIsGovernanceFlagModalVisible(false)}
                />
            )}
        </>
    );
}
